"use client";
import { z } from "zod";

export const loginFormSchema = z.object({
  login: z
    .string()
    .min(1, "loginForm.fields.identifier.error")
    .refine(
      (value) => {
        const isEmail = z.string().email().safeParse(value).success;
        const isPhone = /^\+?[\d\s-]{8,}$/.test(value.replace(/\s/g, ""));
        return isEmail || isPhone;
      },
      {
        message: "loginForm.fields.identifier.error",
      }
    ),
  password: z.string().min(6, "loginForm.fields.password.error"),
});

export const forgotPasswordSchema = z.object({
  email: z
    .string()
    .min(1, "fields.email.required")
    .email("fields.email.invalid"),
});
export const verifyResetCodeSchema = (t: (key: string) => string) =>
  z.object({
    email: z
      .string()
      .min(1, { message: t("fields.email.required") })
      .email({ message: t("fields.email.invalid") }),
    token: z
      .string()
      .min(1, { message: t("fields.token.required") })
      .length(5, { message: t("fields.token.length") })
      .regex(/^\d+$/, { message: t("fields.token.digits") }),
  });

export const resetPasswordSchema = (t: (key: string) => string) =>
  z
    .object({
      email: z.string().email(t("fields.email.invalid")),
      token: z.string().min(1, t("fields.token.required")),
      password: z.string().min(8, {
        message: t("fields.password.error"),
      }),
      password_confirmation: z.string().min(1, {
        message: t("fields.password_confirmation.error"),
      }),
    })
    .refine((data) => data.password === data.password_confirmation, {
      message: t("fields.password_confirmation.mismatch"),
      path: ["password_confirmation"],
    });

export const subscribeFormShema = z.object({
  email: z
    .string()
    .min(1, { message: "Email is required" })
    .email({ message: "Please enter a valid email address" }),
});

export const registerFormSchema = (t: (key: string) => string) =>
  z
    .object({
      first_name: z.string().min(1, t("fields.name.error")),
      last_name: z.string().min(1, t("fields.surname.error")),
      email: z.string().email(t("fields.email.error")),
      phone: z.string().min(1, t("fields.phone.error")),
      country_id: z.string().min(1, t("fields.country.error")),
      city_id: z.string().min(1, t("fields.city.error")),
      password: z.string().min(8, t("fields.password.error")),
      password_confirmation: z
        .string()
        .min(1, t("fields.confirmPassword.error")),
      cv: z
        .instanceof(File, { message: t("fields.cv.errorType") }) // Add custom message for instanceof
        .refine((file) => file.size <= 5 * 1024 * 1024, {
          message: t("fields.cv.errorSize"),
        })
        .refine(
          (file) =>
            [
              "application/pdf",
              "application/msword",
              "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            ].includes(file.type),
          { message: t("fields.cv.errorType") }
        ),
      acceptTerms: z.boolean().refine((val) => val === true, {
        message: t("fields.acceptTerms.error"),
      }),
    })
    .refine((data) => data.password === data.password_confirmation, {
      message: t("fields.confirmPassword.error"),
      path: ["password_confirmation"],
    });

export const passwordChangeSchema = (t: (key: string) => string) =>
  z
    .object({
      current_password: z.string().min(1, {
        message: t("fields.current_password.error"),
      }),
      new_password: z.string().min(6, {
        message: t("fields.new_password.error"),
      }),
      new_password_confirmation: z.string().min(1, {
        message: t("fields.new_password_confirmation.error"),
      }),
    })
    .refine((data) => data.new_password === data.new_password_confirmation, {
      message: t("fields.new_password_confirmation.mismatch"),
      path: ["new_password_confirmation"],
    });

// In your schema definition
export const editProfileSchema = (t: (key: string) => string) =>
  z.object({
    first_name: z.string().min(1, t("form.firstName.required")),
    last_name: z.string().min(1, t("form.lastName.required")),
    email: z.string().email(t("form.email.invalid")),
    phone: z.string().min(1, t("form.phone.required")),
    country_id: z.string().min(1, t("form.country.required")),
    city_id: z.string().min(1, t("form.city.required")),
  });
export const requestServiceFormShema = z.object({
  sub_service_id: z.number(),
  answers: z.record(z.any()),
});
